import { useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import { appConfig } from "../../../configs";
import {
  useBalances,
  useDisclose,
  useHookForm,
  useMerchantApiMutation,
} from "../../../hooks";
import {
  IAccountStatementRequest,
  IHookForm,
  ISwAppApi,
} from "../../../interfaces-and-types";

import { View } from "./View";
import { globalHandlerUtils } from "../../../utils";
import { accountStatementValidationSchema } from "./accountStatementValidationSchema";
import { statementTargets } from "../../../enums";
import { AxiosResponse } from "axios";

// export enum statementKind {
//   wallet = "wallet",
//   payments = "payments",
// }

export type TStatementKind = "wallet" | "payments";
export type TStatementDocumentType = "csv" | "pdf";

export interface IDownloadAccountStatement {
  start_date: string;
  end_date: string;
  currency: string;
  display_payouts: boolean;
  target: statementTargets;
  emails: [{ email: string }];

  // helpers
  statementKind: TStatementKind;
  // includePayoutsInCollectionsStatement: boolean;
  statementDocumentType: TStatementDocumentType;
}

export interface IAccountStatementViewProps
  extends IHookForm<IDownloadAccountStatement> {
  // new top-up
  navigateToNewTopUp: () => void;
  // modal
  openStatementsModal: () => void;
  closeStatementsModal: () => void;
  // others
  setStatementStartPeriod: (startPeriod: string) => void;
  setStatementEndPeriod: (endPeriod: string) => void;
  setStatementKind: (type: TStatementKind) => void;
  setStatementTarget: (type: statementTargets) => void;
  setIncludePayouts: (status: boolean) => void;
  setStatementDocumentType: (type: TStatementDocumentType) => void;

  // email field array
  appendEmailField: () => void;
  removeEmailField: (index: number) => void;
  updateEmailField: (index: number, value: { email: string }) => void;

  // form mgmt
  isLoadingStatement: boolean;
  isErrorStatement: boolean;
  isSuccessStatement: boolean;
  formValues: IDownloadAccountStatement;
}

const handleDownloadFile = (response: AxiosResponse<any, any>) => {
  if (response.status === 200 || response.status === 201) {
    const headers = response.headers;
    const contentType = headers["content-type"];
    const contentDisposition = headers["content-disposition"];
    const fileDownloadName = contentDisposition
      .split(";")[1]
      .split("=")[1]
      .replace('"', "")
      .replace('"', "");

    const downloadUrl = window.URL.createObjectURL(
      new Blob([response.data], { type: contentType })
    );

    const tempLink = document.createElement("a");

    tempLink.href = downloadUrl;
    tempLink.setAttribute("download", fileDownloadName); // any other extension
    document.body.appendChild(tempLink);
    tempLink.click();
    tempLink.remove();

    return response.data;
  }
};

export const AccountStatement = () => {
  const navigate = useNavigate();

  const navigateToBalances = () => {
    navigate("/balances");
  };

  const navigateToStatements = () => {
    navigate("/balances/statements");
  };

  const { activeBalanceCurrency } = useBalances();

  const initialFormValues: IDownloadAccountStatement = {
    start_date: moment()
      .startOf("month") //.format()
      .format(appConfig.javascriptCodeDateFormat),
    end_date: moment()
      .endOf("month") //.format()
      .format(appConfig.javascriptCodeDateFormat),
    currency: activeBalanceCurrency ?? "NGN",
    display_payouts: true,
    emails: [{ email: "" }],

    // helpers
    statementKind: "wallet",
    target: statementTargets.email,
    // includePayoutsInCollectionsStatement: true,
    statementDocumentType: "pdf", // "csv",
  };

  const handleFormSubmit = async ({
    emails,
    ...rest
  }: IDownloadAccountStatement) => {
    const formValuesToSubmit: IAccountStatementRequest = {
      ...rest,
      start_date: moment(formValues.start_date).format(
        appConfig.dotNetDateFormat
      ),
      end_date: moment(formValues.end_date).format(appConfig.dotNetDateFormat),
      emails: formValues.emails.map((em, ind) => em.email),
    };

    if (formValues.statementKind === "payments") {
      await mutateCollectionsStatementAsync(formValuesToSubmit);
    } else {
      await mutateWalletStatementAsync(formValuesToSubmit);
    }
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const {
    onFormSubmit,
    errors,
    register,
    reset,
    control,
    setValue,
    setError,
    // clearErrors,
    watch,
    isDirty,
  } = useHookForm({
    initialFormValues,
    validationSchema: accountStatementValidationSchema,
    onValid: handleFormSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();

  const {
    fields: emails,
    append: appendEmailField,
    remove: removeEmailField,
    update: updateEmailField,
  } = useFieldArray({ name: "emails", control });

  const confirmRemoveEmail = (index: number) => {
    if (window.confirm("Are you sure you want to remove this email?"))
      removeEmailField(index);
  };

  const setStatementKind = (value: TStatementKind) =>
    setValue("statementKind", value);

  const setStatementTarget = (value: statementTargets) =>
    setValue("target", value);

  const setIncludePayouts = (status: boolean) =>
    setValue("display_payouts", status);

  const setStatementDocumentType = (value: TStatementDocumentType) =>
    setValue("statementDocumentType", value);

  const isDownloadTarget = formValues.target === statementTargets.download;

  const collectionsStatementUrl: keyof ISwAppApi =
    formValues.statementDocumentType === "csv"
      ? "collectionsStatementCsv"
      : "collectionsStatementPdf";

  const {
    isLoading: isLoadingCollectionsStatement,
    isSuccess: isSuccessCollectionsStatement,
    isError: isErrorCollectionsStatement,
    // data: collectionsStatement,
    // mutate: mutateCollectionsStatement,
    mutateAsync: mutateCollectionsStatementAsync,
  } = useMerchantApiMutation<IAccountStatementRequest, null>(
    collectionsStatementUrl,
    null,
    {
      parseHttpResponse: !isDownloadTarget ? undefined : handleDownloadFile,
    }
  ).usePostMutation;

  const walletStatementUrl: keyof ISwAppApi =
    formValues.statementDocumentType === "csv"
      ? "walletStatementCsv"
      : "walletStatementPdf";

  const {
    isLoading: isLoadingWalletStatement,
    isSuccess: isSuccessWalletStatement,
    isError: isErrorWalletStatement,
    // data: walletStatement,
    // mutate: mutateWalletStatement,
    mutateAsync: mutateWalletStatementAsync,
  } = useMerchantApiMutation<IAccountStatementRequest, null>(
    walletStatementUrl,
    null,
    {
      parseHttpResponse: !isDownloadTarget ? undefined : handleDownloadFile,
    }
  ).usePostMutation;

  const navigateToNewTopUp = () => {
    navigate("/balances/top-ups/create");
  };

  return (
    <View
      // new top-up
      navigateToNewTopUp={navigateToNewTopUp}
      // modal
      openStatementsModal={navigateToStatements}
      closeStatementsModal={navigateToBalances}
      // other props
      setStatementStartPeriod={(date: string) => setValue("start_date", date)}
      setStatementEndPeriod={(date: string) => setValue("end_date", date)}
      setStatementKind={setStatementKind}
      setStatementTarget={setStatementTarget}
      setIncludePayouts={setIncludePayouts}
      setStatementDocumentType={setStatementDocumentType}
      // email field array
      appendEmailField={() => appendEmailField({ email: "" })}
      removeEmailField={confirmRemoveEmail}
      updateEmailField={updateEmailField}
      // actions
      // downloadCollectionsStatement={downloadCollectionsStatement}
      // isLoadingCollectionsStatement={isLoadingDownloadCollectionsStatement}
      // isErrorCollectionsStatement={isErrorDownloadCollectionsStatement}
      // downloadWalletStatement={downloadWalletStatement}
      // isLoadingWalletStatement={isLoadingDownloadWalletStatement}
      // isErrorWalletStatement={isErrorDownloadWalletStatement}
      isLoadingStatement={
        isLoadingCollectionsStatement || isLoadingWalletStatement
      }
      isErrorStatement={isErrorCollectionsStatement || isErrorWalletStatement}
      isSuccessStatement={
        isSuccessCollectionsStatement || isSuccessWalletStatement
      }
      // form
      // isDirty={isDirty}
      formValues={formValues}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
      control={control}
    />
  );
};
